import SharedPreferences from "../../constants/SharedPreferences";
import { isValidCustomSecurityQuestionKNValues } from "../../util/SecurityQuestionsValidationUtil";
import {
  getErrorMessage,
  generateBCryptSalt,
  saveInStorage,
  getFromStorage,
  convertArrayBufferToBase64String,
  splitUsingSSS,
  splitUsingSSSDynamic,
  generateRandomKey,
  logApiResponse,
  saveInInternalStorage,
  getFromInternalStorage,
  getSigningCurveFromStorage,
  getEncryptionCurveFromStorage,
} from "../../util/Utility";
import {
  getTwoFARestClient,
  getSetTwoFARestClient,
  getNonAuthRestClientForAdmin,
} from "../RestUtility";
import {
  aeadDecryptWithPasscode,
  aeadEncryptWithPasscode,
  generateAesKey,
} from "../../crypto/AuthenticatedEncryption";
import {
  exportEccPrivateKey,
  exportEccPublicKey,
  generateEccKeyPair,
  generateEccSigningKeyPair,
} from "../../crypto/EccEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import { AuthenticateResponse } from "../../BayunCore";
import {
  decryptAsymmetric,
  encryptAsymmetric,
} from "../../util/AsymmetricEncryption";
import {
  appendSignatureAndMetadata,
  getMessageFromSignatureString,
  getUserPublicKeyContext,
  retrieveAndverifyLastSignature,
  signData,
  getUserKeyKekContext,
  getUserSigningPublicKeyContext,
} from "../../util/SignatureUtil";
import { derivePbkdf2Encoded, encodeKey } from "../../crypto/PBKDF2";
import { asRawKey, hmacSha256Raw } from "../../crypto/KeyMaterial";
import SigningPublicKeyTags from "../../constants/SigningPublicKeyTags";
import {
  initializeVishwamSDK,
  faceCaptureRegisterAndLogin,
} from "../VishwamApiManager";
import { getRefreshToken } from "./internal/refreshToken";
import { saveOrgAuthResponseForSwitching } from "./authApi";
import { createMemberAppForSSO, createMemberData } from "./provisioningApi";
import { getMemberClientData } from "./memberApi";
import { restoreUserSigningKeys } from "../../util/UserSigningKeys";
export const createSecurityQuestions = async (
  sessionId,
  email,
  securityQuestions,
  minimumCorrectQuestions,
  isAlsoSetPassphrase,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
) => {
  const totalQuestions = securityQuestions?.length || 0;
  const preparedRequest = await prepareSetSecurityQuestionsRequest(
    sessionId,
    email,
    securityQuestions,
    minimumCorrectQuestions,
    failureCallback,
  );

  // Every rejection inside prepareSetSecurityQuestionsRequest reports itself through
  // failureCallback and then returns nothing. Destructuring that undefined threw a TypeError
  // which propagated past the already-reported failure and became the error the caller surfaced,
  // hiding the real cause.
  if (!preparedRequest) {
    return;
  }

  const {
    securityQuestionsDataRequest,
    validationSecretDataRequest,
    // validationSecretSalt,
    // validationSecretHash,
    userValidationSecret,
    signedUserPublicKeyWithMetadata,
    signedUserSigningPublicKeyWithMetadata,
    userPublicKeyString,
    userPrivateKeyString,
    userSigningPublicKeyString,
    userSigningPrivateKeyString,
  } = preparedRequest;

  // Generate AES userKey
  const userKey = await generateAesKey();

  //Take out userId from localStorage
  const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);
  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);

  //encrypt userKey with userPublicKey
  const userKeyEncryptionResponse = await encryptAsymmetric(
    encodeKey(userKey),
    userPublicKeyString,
    userId,
    encryptionCurve,
  );

  // take out key and kek
  let encryptedUserKey = "";
  let encryptedUserKey_kek = "";

  if (userKeyEncryptionResponse) {
    encryptedUserKey = userKeyEncryptionResponse.encryptedText;
    encryptedUserKey_kek = userKeyEncryptionResponse.keyEncryptionKey;
  }

  // Persist the distinct signing private key with the recovery material.
  const userSigningPrivateKeyEncryptionResponse = await encryptAsymmetric(
    userSigningPrivateKeyString,
    userPublicKeyString,
    userId,
    encryptionCurve,
  );

  if (!userSigningPrivateKeyEncryptionResponse) {
    throw BayunError.ERROR_AUTHENTICATION_FAILED;
  }

  const encryptedUserSigningPrivateKey =
    userSigningPrivateKeyEncryptionResponse.encryptedText;
  const encryptedUserSigningPrivateKeyKek =
    userSigningPrivateKeyEncryptionResponse.keyEncryptionKey;

  // Sign the encrypted user-key KEK with the user signing key.
  const signedUserKeyKEK = await signData(
    encryptedUserKey_kek,
    userSigningPrivateKeyString,
    await getUserKeyKekContext(sessionId),
    await getSigningCurveFromStorage(sessionId),
    sessionId,
  );

  if (!signedUserKeyKEK) {
    console.log("signedUserKeyKEK Signature could not be generated.");
    throw BayunError.ERROR_AUTHENTICATION_FAILED;
  }
  //Add meta data to userKek
  encryptedUserKey_kek = appendSignatureAndMetadata(
    encryptedUserKey_kek,
    signedUserKeyKEK,
    SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG,
    await getUserKeyKekContext(sessionId),
  );

  const registerFaceId = await getFromStorage(
    sessionId,
    SharedPreferences.REGISTER_FACE_ID,
  );

  // initializeVishwamSDK if FR_registered is true
  if (registerFaceId) {
    initializeVishwamSDK(sessionId);
    //  "New User Key is generated again when userAccount is recovered, hence using userKey is not good"
    const frEncryptionKey = encodeKey(await hmacSha256Raw(userKey, userId));
    await saveInStorage(
      sessionId,
      SharedPreferences.FR_ENCRYPTION_KEY,
      frEncryptionKey,
    );
  }

  // -------------------------------    recovery flow
  // Prepare payload for request
  let payload = {
    securityQuestionsDataRequest,
    validationSecretDataRequest,
    // validationSecretSalt,
    // validationSecretHash,
    userValidationSecret,
    userPublicKey: signedUserPublicKeyWithMetadata,
    userSigningPublicKey: signedUserSigningPublicKeyWithMetadata,
    encryptedUserSigningPrivateKey,
    encryptedUserSigningPrivateKeyKek,
    //  "New User Key is generated again when userAccount is recovered, hence using userKey is not good"
    userKey: encryptedUserKey,
    userKeyKek: encryptedUserKey_kek,
    frRegistered: registerFaceId,
  };

  // create a rest client
  let restClient;
  let endPoint;

  if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
    endPoint = "/admin/twofa/setSecurityQuestions";
    restClient = await getNonAuthRestClientForAdmin(sessionId);
  } else {
    endPoint = "/twofa/setSecurityQuestions";
    restClient = await getSetTwoFARestClient(sessionId);
  }

  // call the set security questions API
  try {
    const setSecurityQuestionsResponse = await restClient.post(endPoint, {
      securityQuestionsDataRequest,
      validationSecretDataRequest,
      // validationSecretSalt,
      // validationSecretHash,
      userValidationSecret,
      userPublicKey: signedUserPublicKeyWithMetadata,
      userSigningPublicKey: signedUserSigningPublicKeyWithMetadata,
      encryptedUserSigningPrivateKey,
      encryptedUserSigningPrivateKeyKek,
      //  "New User Key is generated again when userAccount is recovered, hence using userKey is not good"
      userKey: encryptedUserKey,
      userKeyKek: encryptedUserKey_kek,
      frRegistered: registerFaceId,
      totalQuestions,
      minimumCorrectQuestions,
    });

    // console.log(setSecurityQuestionsResponse);
    await logApiResponse(
      "setSecurityQuestionsResponse",
      setSecurityQuestionsResponse,
    );
    if (setSecurityQuestionsResponse.data.secret) {
      var secret = setSecurityQuestionsResponse.data.secret;
      await saveInStorage(sessionId, SharedPreferences.VISHWAM_SECRET, secret);
    }

    const recoveryFlow = await getFromStorage(sessionId, SharedPreferences.RECOVERY_FLOW);

    const isRecoveryFlow =
      recoveryFlow === Constants.USER_RECOVERY_STATUS.SET_CREDENTIALS ||
      recoveryFlow === Constants.USER_RECOVERY_STATUS.RECOVERY_STARTED;

    // Completing a recovery is not a first login, so the first-login prompts (the recovery-key
    // download offer) must not be shown again straight afterwards.
    //
    // The condition was previously written as
    //   SOURCE == BAYUN_ADMIN && flow === SET_CREDENTIALS || flow === RECOVERY_STARTED
    // which parses as `(SOURCE && SET_CREDENTIALS) || RECOVERY_STARTED` because && binds tighter
    // than ||. The SOURCE guard therefore applied to only one of the two states, and
    // RECOVERY_STARTED took this branch for every SOURCE - the opposite of the intent. Wrong since
    // it was introduced (e9821faba), not a regression.
    //
    // Rewriting it is behaviour-preserving for every reachable state. The only case that changes is
    // BASE_KIT + RECOVERY_STARTED, which cannot get here: while a user is in RECOVERY_STARTED the
    // server rejects the third-party login path outright (twoFaApi rejects it, and getMemberInfo
    // throws USER_RECOVERY_IN_PROGRESS), so security questions cannot be reached to be set.
    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN && isRecoveryFlow) {
      // Note: the accompanying `saveInStorage(RECOVERY_FLOW, null)` is commented out below, so the
      // recovery flag survives credential setup. Left as-is deliberately - re-enabling it changes
      // the state machine and deserves its own decision.
      await saveInStorage(sessionId, SharedPreferences.FIRST_TIME_LOG_IN, false);
      // await saveInStorage(sessionId, SharedPreferences.RECOVERY_FLOW, null);
    } else {
      await saveInStorage(sessionId, SharedPreferences.FIRST_TIME_LOG_IN, true);
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.USER_PUBLIC_KEY,
      userPublicKeyString,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PUBLIC_KEY,
      userSigningPublicKeyString,
    );

    // setting here to get access in passphrase set method
    await saveInInternalStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      userPrivateKeyString,
    );
    await saveInInternalStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PRIVATE_KEY,
      userSigningPrivateKeyString,
    );

    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKeyString,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_SIGNING_PRIVATE_KEY,
        userSigningPrivateKeyString,
      );

      successCallback({
        sessionId,
        email,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
      return;
    }

    if (!isAlsoSetPassphrase) {
      // This will be only called when a new app comes and user does not exist
      if (
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER_APP,
        )
      ) {
        await createMemberAppForSSO(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          userPrivateKeyString,
          failureCallback,
        );
        if (
          !(await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID))
        ) {
          failureCallback(BayunError.ERROR_LINK_USER_MEMBER_ON_ADMIN);
          return;
        }
      } else {
        const memberRegistrationComplete = await createMemberData(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          null,
          await getFromStorage(sessionId, SharedPreferences.APP_ID),
          await getFromInternalStorage(sessionId, SharedPreferences.APP_SECRET),
          await getFromStorage(sessionId, SharedPreferences.APP_KEY_PAIR_ID),
          await getFromStorage(sessionId, SharedPreferences.GHOST_MEMBER_ID),
          await getFromStorage(
            sessionId,
            SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
          ),
          await getFromStorage(sessionId, SharedPreferences.ADMIN_PUBLIC_KEY),
          await getFromStorage(
            sessionId,
            SharedPreferences.IS_CREATE_ORG_REQUEST,
          ),
          email,
          userPrivateKeyString,
          authorizeMemberCallback,
          failureCallback,
        );
        if (!memberRegistrationComplete) {
          return;
        }
      }
      if (await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID)) {
        await faceCaptureRegisterAndLogin(
          "true",
          successCallback,
          failureCallback,
        );
      } else {
        successCallback({
          sessionId,
          securityQuestions: null,
          authenticationResponse: AuthenticateResponse.SUCCESS,
        });
      }
    } else {
      await successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
    }
  } catch (error) {
    if (error?.bayunAlreadyHandled) {
      return;
    }
    console.log(error);
    if (error?.response?.data) {
      failureCallback(getErrorMessage(sessionId, error.response.data));
    } else if (typeof error === "string") {
      failureCallback(error);
    } else {
      failureCallback(error?.message || BayunError.ERROR_SOMETHING_WENT_WRONG);
    }
  }
};

export async function prepareSetSecurityQuestionsRequest(
  sessionId,
  email,
  securityQuestions,
  minimumCorrectQuestions = 3,
  failureCallback,
) {
  securityQuestions.forEach((element) => {
    element.answer = element.answer.trim().toLowerCase();
  });

  const normalizedTotalQuestions = securityQuestions?.length || 0;
  const normalizedMinimumCorrectQuestions = Number(minimumCorrectQuestions) || 3;

  if (
    !isValidCustomSecurityQuestionKNValues(
      normalizedTotalQuestions,
      normalizedMinimumCorrectQuestions,
    )
  ) {
    failureCallback(BayunError.ERROR_INVALID_K_N_VALUES);
    return;
  }

  for (let i = 0; i < normalizedTotalQuestions; i++) {
    if (!securityQuestions[i].question || !securityQuestions[i].answer) {
      failureCallback(BayunError.ERROR_INCOMPLETE_SECURITY_QUESTIONS);
      return;
    }
  }

  // The server resolves the userPublicKey and userSigningPublicKey signature layers by their
  // context, which is derived from the user's email. Signing without it yields a layer the
  // server can never find.
  if (!email) {
    failureCallback(BayunError.ERROR_REAUTHENTICATION_NEEDED);
    return;
  }

  let userPrivateKeyString;
  let userPublicKeyString;
  let userSigningPrivateKeyString;
  let userSigningPublicKeyString;
  // create a user key pair if not exist

  if (await getFromStorage(sessionId, SharedPreferences.USER_PRIVATE_KEY)) {
    userPrivateKeyString = await getFromStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
    );
    userPublicKeyString = await getFromStorage(
      sessionId,
      SharedPreferences.USER_PUBLIC_KEY,
    );
  } else {
    // create a user key pair
    const userKeyPair = await generateEccKeyPair(
      await getEncryptionCurveFromStorage(sessionId),
    );
    userPrivateKeyString = await exportEccPrivateKey(userKeyPair.privateKey);
    userPublicKeyString = await exportEccPublicKey(userKeyPair.publicKey);
  }

  if (
    (await getFromStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PRIVATE_KEY,
    )) &&
    (await getFromStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PUBLIC_KEY,
    ))
  ) {
    userSigningPrivateKeyString = await getFromStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PRIVATE_KEY,
    );
    userSigningPublicKeyString = await getFromStorage(
      sessionId,
      SharedPreferences.USER_SIGNING_PUBLIC_KEY,
    );
  } else {
    const signingCurve = await getSigningCurveFromStorage(sessionId);
    const userSigningKeyPair =
      await generateEccSigningKeyPair(signingCurve);
    userSigningPrivateKeyString = await exportEccPrivateKey(
      userSigningKeyPair.privateKey,
    );
    userSigningPublicKeyString = await exportEccPublicKey(
      userSigningKeyPair.publicKey,
    );
  }

  // // create a user key pair
  // const userKeyPair = await generateEccKeyPair();
  // const userPrivateKeyString = await exportEccPrivateKey(
  //   userKeyPair.privateKey
  // );
  // const userPublicKeyString = await exportEccPublicKey(userKeyPair.publicKey);

  // split the userPrivateKey into N parts using SSS with K threshold
  const userPrivateKeyPartsMap =
    normalizedTotalQuestions === 5 && normalizedMinimumCorrectQuestions === 3
      ? splitUsingSSS(userPrivateKeyString)
      : splitUsingSSSDynamic(
        userPrivateKeyString,
        normalizedTotalQuestions,
        normalizedMinimumCorrectQuestions,
      );

  // generate answer salts, authAnswerHashes and encrypt userPrivateKeyPart with each answer
  const securityQuestionsDataRequest = [];

  for (let i = 0; i < normalizedTotalQuestions; i++) {
    securityQuestions[i].answer = securityQuestions[i].answer.toLowerCase();

    // const authAnswerSalt = await generateBCryptSalt();
    const keyAnswerSalt = await generateBCryptSalt();
    // const authAnswerHash = await derivePbkdf2Encoded(
    //   securityQuestions[i].answer,
    //   authAnswerSalt,
    // );
    const encryptedUserPrivateKeyPart = await aeadEncryptWithPasscode(
      convertArrayBufferToBase64String(userPrivateKeyPartsMap.get(`${i + 1}`)),
      securityQuestions[i].answer,
      keyAnswerSalt,
      `${i + 1}`,
    );

    // add to request object
    securityQuestionsDataRequest.push({
      questionId: `${i + 1}`,
      keyAnswerSalt,
      encryptedUserPrivateKeyPart,
      // authAnswerSalt,
      // authAnswerHash,
      questionText: securityQuestions[i].question,
    });
  }



  const userValidationSecret = await generateRandomKey();


  // const validationSecretSalt = await generateBCryptSalt();
  // const validationSecretHash = await generateHMacHash(
  //   userValidationSecret,
  //   validationSecretSalt,
  // );

  // split the userValidationSecret into N parts using SSS with K threshold
  const userValidationSecretPartsMap =
    normalizedTotalQuestions === 5 && normalizedMinimumCorrectQuestions === 3
      ? splitUsingSSS(userValidationSecret)
      : splitUsingSSSDynamic(
        userValidationSecret,
        normalizedTotalQuestions,
        normalizedMinimumCorrectQuestions,
      );

  const validationSecretDataRequest = [];

  for (let i = 0; i < normalizedTotalQuestions; i++) {
    const keyValidationSecretSalt = await generateBCryptSalt();
    const encryptedValidationSecretPart = await aeadEncryptWithPasscode(
      convertArrayBufferToBase64String(
        userValidationSecretPartsMap.get(`${i + 1}`),
      ),
      securityQuestions[i].answer,
      keyValidationSecretSalt,
      `${i + 1}`,
    );
    validationSecretDataRequest.push({
      questionId: `${i + 1}`,
      keyValidationSecretSalt,
      encryptedValidationSecretPart,
    });
  }

  const signingCurve = await getSigningCurveFromStorage(sessionId);

  // Sign the user encryption public key with the distinct user signing key.
  const signedUserPublicKey = await signData(
    userPublicKeyString,
    userSigningPrivateKeyString,
    getUserPublicKeyContext(email),
    signingCurve,
    sessionId,
  );

  if (!signedUserPublicKey) {
    console.log("signedUserPublicKey signature could not be generated.");
    failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
    return;
  }

  var signedUserPublicKeyWithMetadata = appendSignatureAndMetadata(
    userPublicKeyString,
    signedUserPublicKey,
    SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG,
    getUserPublicKeyContext(email),
  );

  const userSigningPublicKeyContext =
    getUserSigningPublicKeyContext(email);
  const signedUserSigningPublicKey = await signData(
    userSigningPublicKeyString,
    userSigningPrivateKeyString,
    userSigningPublicKeyContext,
    signingCurve,
    sessionId,
  );

  if (!signedUserSigningPublicKey) {
    console.log("signedUserSigningPublicKey signature could not be generated.");
    failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
    return;
  }

  const signedUserSigningPublicKeyWithMetadata =
    appendSignatureAndMetadata(
      userSigningPublicKeyString,
      signedUserSigningPublicKey,
      SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG,
      userSigningPublicKeyContext,
    );

  return {
    securityQuestionsDataRequest,
    validationSecretDataRequest,
    userValidationSecret,
    signedUserPublicKeyWithMetadata,
    signedUserSigningPublicKeyWithMetadata,
    userPublicKeyString,
    userPrivateKeyString,
    userSigningPublicKeyString,
    userSigningPrivateKeyString,
  };
}

export async function prepareSetNewPassphraseRequest(sessionId, passphrase) {
  // create user salts and auth hash
  const userAuthSalt = await generateBCryptSalt();
  const userKeySalt = await generateBCryptSalt();
  const authPassphraseHash = await derivePbkdf2Encoded(passphrase, userAuthSalt);

  let userPrivateKey = await getFromInternalStorage(
    sessionId,
    SharedPreferences.USER_PRIVATE_KEY,
  );

  if (!userPrivateKey) {
    userPrivateKey = await getFromStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
    );
  }

  const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);

  // encrypt the userPrivateKey with passphrase
  const encryptedUserPrivateKey = await aeadEncryptWithPasscode(
    userPrivateKey,
    passphrase,
    userKeySalt,
    userId,
  );

  return {
    authPassphraseHash,
    encryptedUserPrivateKey,
    userAuthSalt,
    userKeySalt,
    userPrivateKey,
  };
}

export const createPassphrase = async (
  sessionId,
  email,
  passphrase,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
) => {
  if (!passphrase) {
    failureCallback(BayunError.ERROR_PASSPHRASE_CANNOT_BE_NULL);
    return;
  }

  const {
    authPassphraseHash,
    encryptedUserPrivateKey,
    userAuthSalt,
    userKeySalt,
    userPrivateKey,
  } = await prepareSetNewPassphraseRequest(sessionId, passphrase);

  // create a rest client
  let restClient;
  let endPoint;

  if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
    endPoint = "/admin/twofa/setPassphrase";
    restClient = await getNonAuthRestClientForAdmin(sessionId);
  } else {
    endPoint = "/twofa/setPassphrase";
    restClient = await getSetTwoFARestClient(sessionId);
  }

  // call the set security questions API
  try {
    const setPassphraseResponse = await restClient.post(endPoint, {
      authPassphraseHash,
      encryptedUserPrivateKey,
      userAuthSalt,
      userKeySalt,
    });

    // console.log(setPassphraseResponse);
    await logApiResponse("setPassphraseResponse", setPassphraseResponse);

    await saveInInternalStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      userPrivateKey,
    );

    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKey,
      );

      successCallback({
        sessionId,
        email,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
      return;
    }
    // decrypted
    // await updateLocalStorageKeyForPassphrase(sessionId, passphrase);
    // This will be only called when a new app comes and user does not exist
    if (
      await getFromStorage(
        sessionId,
        SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER_APP,
      )
    ) {
      await createMemberAppForSSO(
        sessionId,
        await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
        await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        userPrivateKey,
        failureCallback,
      );
      if (
        !(await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID))
      ) {
        failureCallback(BayunError.ERROR_LINK_USER_MEMBER_ON_ADMIN);
        return;
      }
    } else {
      const memberRegistrationComplete = await createMemberData(
        sessionId,
        await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
        await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        null,
        await getFromStorage(sessionId, SharedPreferences.APP_ID),
        await getFromInternalStorage(sessionId, SharedPreferences.APP_SECRET),
        await getFromStorage(sessionId, SharedPreferences.APP_KEY_PAIR_ID),
        await getFromStorage(sessionId, SharedPreferences.GHOST_MEMBER_ID),
        await getFromStorage(
          sessionId,
          SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
        ),
        await getFromStorage(sessionId, SharedPreferences.ADMIN_PUBLIC_KEY),
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_CREATE_ORG_REQUEST,
        ),
        email,
        userPrivateKey,
        authorizeMemberCallback,
        failureCallback,
      );
      if (!memberRegistrationComplete) {
        return;
      }
    }
    if (await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID)) {
      await faceCaptureRegisterAndLogin(
        "true",
        successCallback,
        failureCallback,
      );
    } else {
      await successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
    }
  } catch (error) {
    if (error?.bayunAlreadyHandled) {
      return;
    }
    if (error?.response?.data) {
      failureCallback(getErrorMessage(sessionId, error.response.data));
    } else if (typeof error === "string") {
      failureCallback(error);
    } else {
      failureCallback(error?.message || BayunError.ERROR_SOMETHING_WENT_WRONG);
    }
  }
};

export const validatePassphraseAPI = async (
  sessionId,
  passphrase,
  // passcode,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
) => {
  const userAuthSalt = await getFromStorage(
    sessionId,
    SharedPreferences.USER_AUTH_SALT,
  );

  const authPassphraseHash = await derivePbkdf2Encoded(passphrase, userAuthSalt);

  try {
    let validatePassphraseResponse;
    let memberAppId;
    let twoFAClient;

    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      twoFAClient = getNonAuthRestClientForAdmin(sessionId);
      validatePassphraseResponse = await twoFAClient.post(
        "/admin/twofa/validatePassphrase",
        { authPassphraseHash },
      );
    } else {
      const appId = await getFromStorage(sessionId, SharedPreferences.APP_ID);
      const appSecret = await getFromStorage(
        sessionId,
        SharedPreferences.APP_SECRET,
      );

      twoFAClient = getTwoFARestClient(appId, sessionId);
      memberAppId = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_APP_ID,
      );

      validatePassphraseResponse = await twoFAClient.post(
        "/twofa/validatePassphrase",
        { authPassphraseHash, memberAppId },
      );
    }


    // console.log(validatePassphraseResponse);
    await logApiResponse(
      "validatePassphraseResponse",
      validatePassphraseResponse,
    );


    await saveInStorage(sessionId, SharedPreferences.FIRST_TIME_LOG_IN, validatePassphraseResponse.data.isfirstComponentRenderd);

    if (memberAppId !== null && memberAppId !== undefined) {
      await getRefreshToken(validatePassphraseResponse, twoFAClient);
    }
    let userId;

    if (!validatePassphraseResponse.data.userId) {
      userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);
    } else {
      userId = getMessageFromSignatureString(
        validatePassphraseResponse.data.userId,
      );
    }

    // save isDev account
    if (validatePassphraseResponse.data.isDeveloper) {
      await saveInStorage(
        sessionId,
        SharedPreferences.IS_DEVELOPER,
        validatePassphraseResponse.data.isDeveloper,
      );
    }

    if (process.env.SOURCE != Constants.SOURCE.BAYUN_ADMIN) {
      // verify and save userId
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          validatePassphraseResponse.data.userId,
        ))
      ) {
        console.log("userId verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        return;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.USER_ID,
          getMessageFromSignatureString(validatePassphraseResponse.data.userId),
        );
      }
    }

    // need to recheck
    if (
      process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN &&
      !validatePassphraseResponse.data.developerKey_kek
    ) {
      await saveInStorage(sessionId, SharedPreferences.IS_USER, "true");
    }

    // decrypt the userPrivateKey
    const userPrivateKey = await aeadDecryptWithPasscode(
      validatePassphraseResponse.data.userPrivateKey,
      passphrase,
      validatePassphraseResponse.data.userKeySalt,
      userId,
    );

    await saveInInternalStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      userPrivateKey,
    );

    // save userPublicKey
    if (validatePassphraseResponse.data.userPublicKey) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PUBLIC_KEY,
        getMessageFromSignatureString(
          validatePassphraseResponse.data.userPublicKey,
        ),
      );
    }

    // Restore the user signing identity. Without it the session can decrypt but
    // cannot sign, and the failure would only surface later in an unrelated flow.
    // Admin-initiated SET_CREDENTIALS recovery is no exception: the admin escrows the signing
    // key it used to sign userPublicKey, userKeyKek and every memberPrivateKey_kek, and the
    // server releases it here wrapped to the same encryption key just recovered above. The
    // recovering user adopts it, so those signatures stay verifiable.
    await restoreUserSigningKeys(
      sessionId,
      validatePassphraseResponse.data,
      userPrivateKey,
      userId,
    );

    if (
      validatePassphraseResponse.data.frRegistered == "true" &&
      validatePassphraseResponse.data.frEnabled == "true" &&
      process.env.INCLUDE_VISHWAM == true
    ) {
      //  "New User Key is generated again when userAccount is recovered,hence using userKey is not good"
      const rawUserKey = asRawKey(
        await decryptAsymmetric(
          validatePassphraseResponse.data.userKey,
          userPrivateKey,
          validatePassphraseResponse.data.userKeyKek,
          await getFromStorage(sessionId, SharedPreferences.USER_ID),
          await getEncryptionCurveFromStorage(sessionId),
        ),
      );

      //  "New User Key is generated again when userAccount is recovered,hence using userKey is not good"
      const frDecryptionKey = encodeKey(
        await hmacSha256Raw(
          rawUserKey,
          await getFromStorage(sessionId, SharedPreferences.USER_ID),
        ),
      );

      await saveInStorage(
        sessionId,
        SharedPreferences.FR_DECRYPTION_KEY,
        frDecryptionKey,
      );
      await saveInStorage(sessionId, SharedPreferences.REGISTER_FACE_ID, true);
      initializeVishwamSDK(sessionId);
    }

    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKey,
      );

      // verify and save developerKey_kek
      if (validatePassphraseResponse.data.developerKey_kek) {
        if (
          !(await retrieveAndverifyLastSignature(
            sessionId,
            validatePassphraseResponse.data.developerKey_kek,
          ))
        ) {
          console.log("developerKey_kek verification failed.");
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        } else {
          console.log("developerKey_kek verification success.");
          await saveInStorage(
            sessionId,
            SharedPreferences.DEVELOPER_KEY_KEK,
            getMessageFromSignatureString(
              validatePassphraseResponse.data.developerKey_kek,
            ),
          );
        }

        const developerKey = await decryptAsymmetric(
          validatePassphraseResponse.data.developerKey,
          userPrivateKey,
          getMessageFromSignatureString(
            validatePassphraseResponse.data.developerKey_kek,
          ),
          await getFromStorage(sessionId, SharedPreferences.USER_ID),
          await getEncryptionCurveFromStorage(sessionId),
        );

        if (!developerKey) {
          failureCallback(BayunError.ERROR_DEVELOPER_KEY_VERIFICATION_FAILED);
        } else {
          // decrypt yields Base64 from encodeKey at encrypt time; keep encoded in storage
          const developerKeyEncoded =
            typeof developerKey === "string"
              ? developerKey
              : encodeKey(developerKey);
          await saveInStorage(
            sessionId,
            SharedPreferences.DEVELOPER_KEY,
            developerKeyEncoded,
          );
        }
      }

      await saveOrgAuthResponseForSwitching(
        sessionId,
        validatePassphraseResponse,
        failureCallback,
      );

      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
      return;
    }

    if (await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID)) {
      // setPasscode(passcode);
      await faceCaptureRegisterAndLogin(
        "false",
        successCallback,
        failureCallback,
      );
    } else {
      // This will be called when new app comes and user exist.
      if (
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER_APP,
        )
      ) {
        await createMemberAppForSSO(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          userPrivateKey,
          failureCallback,
        );
      } else if (
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER,
        )
      ) {
        await createMemberData(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          null,
          await getFromStorage(sessionId, SharedPreferences.APP_ID),
          await getFromInternalStorage(sessionId, SharedPreferences.APP_SECRET),
          await getFromStorage(sessionId, SharedPreferences.APP_KEY_PAIR_ID),
          await getFromStorage(sessionId, SharedPreferences.GHOST_MEMBER_ID),
          await getFromStorage(
            sessionId,
            SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
          ),
          await getFromStorage(sessionId, SharedPreferences.ADMIN_PUBLIC_KEY),
          await getFromStorage(
            sessionId,
            SharedPreferences.IS_CREATE_ORG_REQUEST,
          ),

          await getFromStorage(sessionId, SharedPreferences.EMAIL_ADDRESS),
          userPrivateKey,
          authorizeMemberCallback,
          failureCallback,
        );
      } else {
        await getMemberClientData(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          // passcode
          null,
          failureCallback,
        );
      }

      // removeLoadMask();
      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
    }
  } catch (error) {
    // removeLoadMask();
    if (error?.bayunAlreadyHandled) {
      return;
    }
    if (error?.response?.data) {
      failureCallback(getErrorMessage(sessionId, error.response.data));
    } else if (typeof error === "string") {
      failureCallback(error);
    } else {
      failureCallback(error?.message || BayunError.ERROR_SOMETHING_WENT_WRONG);
    }
  }
};
